// src/services/pdf/generateConfirmacion.js
// Improved Event Confirmation / Production Guide PDF.
// Refactored from original pdfService.js to use shared branding.

import { jsPDF } from "jspdf";
import {
    preloadLogos,
    drawHeader,
    drawFooter,
    drawSectionTitle,
    drawInfoTable,
    drawBulletList,
    drawDataBox,
    checkPageBreak,
} from "./pdfBranding.js";
import {
    COMPANY,
    COLORS,
    formatCurrency,
    formatDate,
    todayFormatted,
    generateDocNumber,
    safeFileName,
} from "./pdfConfig.js";

/**
 * Generate an improved Event Confirmation / Production Guide PDF.
 * @param {object} event       - Datos del evento
 * @param {object} contact     - Contacto principal { name, email, phone, nif }
 * @param {object} companyData - Datos de la empresa (CIF, IBAN, etc.)
 * @param {string} docNumber   - Número de documento ya generado/incrementado
 */
export async function generateConfirmacionPDF(event, contact = {}, companyData = {}, docNumber = "") {

    if (!event) {
        console.error("No event data for Confirmación");
        return;
    }

    await preloadLogos();
    const doc = new jsPDF({ compress: true });
    const finalDocNumber = docNumber || generateDocNumber("CONF"); 
    const pageWidth = doc.internal.pageSize.getWidth();


    // === HEADER ===
    let y = drawHeader(doc, {
        title: "CONFIRMACIÓN",
        docNumber: finalDocNumber,
        subtitle: "Documento de Confirmación y Guía de Producción 2026",
    });


    // === SECTION 1: EVENT SUMMARY ===
    y = drawSectionTitle(doc, y, "RESUMEN DEL EVENTO", { number: "1" });

    y = drawInfoTable(doc, y, [
        { label: "Fecha del evento:", value: formatDate(event.date) },
        { label: "Ubicación:", value: event.city || event.location || event.region || "—" },
        { label: "Cliente:", value: contact.name || event.client_name_legacy || "—" },
        { label: "Contacto:", value: contact.email || "—" },
        { label: "Teléfono:", value: contact.phone || "—" },
        { label: "Tipo:", value: capitalize(event.type || "—") },
    ]);

    y += 4;

    // === FINANCIAL SUMMARY ===
    y = checkPageBreak(doc, y, 35);

    const total = Number(event.amount || 0);

    // Price box
    doc.setFillColor(235, 245, 235);
    doc.roundedRect(15, y, pageWidth - 30, 20, 2, 2, "F");
    doc.setDrawColor(...COLORS.SUCCESS);
    doc.setLineWidth(0.5);
    doc.roundedRect(15, y, pageWidth - 30, 20, 2, 2, "S");

    doc.setTextColor(0, 100, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.text("ESTADO: CONFIRMADO", 20, y + 7);

    doc.setTextColor(...COLORS.TEXT);
    doc.setFontSize(10);
    doc.text(`Precio acordado: ${formatCurrency(total)} (sin IVA)`, 20, y + 14);

    y += 28;

    // === SECTION 2: COMMITMENTS ===
    y = checkPageBreak(doc, y, 40);
    y = drawSectionTitle(doc, y, "NUESTROS COMPROMISOS", { number: "2" });

    y = drawBulletList(doc, y, [
        "Actuación completa del grupo Los Toldos con formación estándar.",
        "Backline completo (instrumentos y amplificación propia).",
        "Sistema de monitoraje in-ear (IEM) profesional incluido.",
        "Llegada al venue con un mínimo de 2 horas de antelación.",
    ]);

    y += 5;

    // === SECTION 3: TECH REQUIREMENTS ===
    y = checkPageBreak(doc, y, 50);
    y = drawSectionTitle(doc, y, "REQUISITOS TÉCNICOS (Obligaciones del Cliente)", { number: "3" });

    const techSections = [
        {
            title: "ESCENARIO",
            items: [
                "Dimensiones mínimas: 6x3 metros o 5x4 metros.",
                "Altura: entre 20-50 cm.",
                "Debe estar montado y listo ANTES de la llegada del grupo.",
            ],
        },
        {
            title: "SONIDO E ILUMINACIÓN",
            items: getAudioItems(event),
        },
        {
            title: "ELECTRICIDAD",
            items: ["Toma de corriente independiente y accesible cerca del escenario."],
        },
        {
            title: "LOGÍSTICA",
            items: [
                "1 plaza de parking para furgoneta Mercedes Vito.",
                "Acceso al lugar 2 horas antes del show para prueba de sonido.",
            ],
        },
    ];

    techSections.forEach((sec) => {
        y = checkPageBreak(doc, y, 25);
        doc.setTextColor(...COLORS.DARK);
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text(sec.title, 18, y);
        y += 6;

        y = drawBulletList(doc, y, sec.items, { startX: 22 });
        y += 3;
    });

    // === SECTION 4: LEGAL ===
    y = checkPageBreak(doc, y, 50);
    y = drawSectionTitle(doc, y, "CONDICIONES LEGALES", { number: "4" });

    // Warning for outside Vizcaya
    if (!isVizcaya(event) && (event.city || event.location)) {
        y = checkPageBreak(doc, y, 20);
        doc.setFillColor(255, 245, 235);
        doc.roundedRect(15, y, pageWidth - 30, 15, 2, 2, "F");
        doc.setDrawColor(...COLORS.WARNING);
        doc.setLineWidth(0.5);
        doc.roundedRect(15, y, pageWidth - 30, 15, 2, 2, "S");

        doc.setTextColor(180, 80, 0);
        doc.setFontSize(9);
        doc.setFont(undefined, "bold");
        doc.text("⚠ RECUERDA: Fuera de Vizcaya, el equipo de sonido (P.A.)", 20, y + 6);
        doc.text("y técnico de sonido NO están incluidos.", 24, y + 11);
        y += 20;
    }

    const legalItems = [
        "La reserva es firme tras el ingreso de 500€ en concepto de señal.",
        "El resto del importe se abona el día del servicio.",
        "Cancelación con menos de 30 días: se pierde la señal.",
        "Cancelación con menos de 7 días: se devenga el 100% de la tarifa.",
        "Los Toldos se reserva el derecho de grabar y fotografiar el evento con fines promocionales.",
        "Todos los precios están expresados SIN IVA.",
    ];

    y = drawBulletList(doc, y, legalItems);

    y += 8;

    // === CLOSING NOTE ===
    y = checkPageBreak(doc, y, 25);
    doc.setFillColor(...COLORS.LIGHT_BG);
    doc.roundedRect(15, y, pageWidth - 30, 18, 2, 2, "F");
    doc.setDrawColor(...COLORS.GRAY);
    doc.setLineWidth(0.3);
    doc.roundedRect(15, y, pageWidth - 30, 18, 2, 2, "S");

    doc.setTextColor(...COLORS.DARK);
    doc.setFontSize(9);
    doc.setFont(undefined, "italic");
    doc.text("Para cualquier duda o aclaración, contacta con nosotros.", 20, y + 6);
    doc.text(`${companyData.email || COMPANY.email}  ·  ${companyData.phone || COMPANY.phone}`, 20, y + 12);


    // === FOOTER ===
    drawFooter(doc);

    // === SAVE ===
    const safeName = safeFileName(event.client_name_legacy);
    doc.save(`Confirmacion_${safeName}_${finalDocNumber}.pdf`);

}

// =========================
// Helpers
// =========================
function getAudioItems(event) {
    if (isVizcaya(event)) {
        return [
            "El grupo proporcionará equipo de sonido para eventos en Vizcaya.",
            "Iluminación profesional: consultar disponibilidad.",
        ];
    }
    return [
        "Sistema de sonido profesional (P.A.) con micrófonos — a cargo del cliente.",
        "Iluminación profesional — a cargo del cliente.",
        "Debe ser proporcionado por empresa especializada.",
    ];
}

function capitalize(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function isVizcaya(ev) {
    const loc = (ev.city || ev.location || ev.region || "").toLowerCase();
    return (
        loc.includes("vizcaya") ||
        loc.includes("bizkaia") ||
        loc.includes("bilbao") ||
        loc.includes("getxo")
    );
}
